import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import {
  AdvancedSearchHistoryListDto,
  ApiService,
  ParishListResponseDto,
  SearchAddressGeometryDto,
  SearchAddressResultDto,
  SearchAddressRoadOptionsDto,
  SearchCrownDescriptionGeometryDto,
  SearchCrownDescriptionParcelResultDto,
  SearchLotResultDto,
  SearchParcelBySpiResultDto,
  SearchParcelGeometryDto,
  SearchParcelResultDto,
  SearchPropertyByCouncilNoResultDto,
  SearchPropertyGeometryDto,
  SearchPropertyLgaOptionsDto,
  SearchPropertyResultDto,
  SearchSurveyLabelDto,
  SearchSurveyMarkDto,
  SearchSurveyMarkGeometryDto,
  TownshipListResponseDto,
} from '@core/api/angular';
import { createAdvancedSearchHistory } from '@core/api/angular/operations/create-advanced-search-history';
import { deleteAdvancedSearchHistory } from '@core/api/angular/operations/delete-advanced-search-history';
import { getAdvancedSearchHistory } from '@core/api/angular/operations/get-advanced-search-history';
import { getParishList } from '@core/api/angular/operations/get-parish-list';
import { getRoadSearchOptions } from '@core/api/angular/operations/get-road-search-options';
import { getTownshipList } from '@core/api/angular/operations/get-township-list';
import { markAdvancedSearchHistoryAsUsed } from '@core/api/angular/operations/mark-advanced-search-history-as-used';
import { searchForCrownDescriptionGeometry } from '@core/api/angular/operations/search-for-crown-description-geometry';
import { searchForCrownDescriptionParcels } from '@core/api/angular/operations/search-for-crown-description-parcels';
import { searchForLocality } from '@core/api/angular/operations/search-for-locality';
import { searchForLocalityGeometry } from '@core/api/angular/operations/search-for-locality-geometry';
import { searchForLotByPlan } from '@core/api/angular/operations/search-for-lot-by-plan';
import { searchForLotByStreet } from '@core/api/angular/operations/search-for-lot-by-street';
import { searchForParcelByPfi } from '@core/api/angular/operations/search-for-parcel-by-pfi';
import { searchForParcelBySpi } from '@core/api/angular/operations/search-for-parcel-by-spi';
import { searchForParcelByViewPfi } from '@core/api/angular/operations/search-for-parcel-by-view-pfi';
import { searchForParcelGeometryByPfiList } from '@core/api/angular/operations/search-for-parcel-geometry-by-pfi-list';
import { searchForProperty } from '@core/api/angular/operations/search-for-property';
import { searchForPropertyByAddressPfi } from '@core/api/angular/operations/search-for-property-by-address-pfi';
import { searchForPropertyByCouncilPropertyNo } from '@core/api/angular/operations/search-for-property-by-council-property-no';
import { searchForPropertyByLgaOptions } from '@core/api/angular/operations/search-for-property-by-lga-options';
import { searchForPropertyByPfi } from '@core/api/angular/operations/search-for-property-by-pfi';
import { searchForPropertyByViewPfi } from '@core/api/angular/operations/search-for-property-by-view-pfi';
import { searchForPropertyGeometry } from '@core/api/angular/operations/search-for-property-geometry';
import { searchForPropertyGeometryByPfiList } from '@core/api/angular/operations/search-for-property-geometry-by-pfi-list';
import { searchForRoad } from '@core/api/angular/operations/search-for-road';
import { searchForRoadGeometry } from '@core/api/angular/operations/search-for-road-geometry';
import { searchForSurveyLabel } from '@core/api/angular/operations/search-for-survey-label';
import { searchForSurveyLabelGeometryByPfiList } from '@core/api/angular/operations/search-for-survey-label-geometry-by-pfi-list';
import { searchForSurveyMark } from '@core/api/angular/operations/search-for-survey-mark';
import { searchForSurveyMarkGeometryByPfiList } from '@core/api/angular/operations/search-for-survey-mark-geometry-by-pfi-list';
import { format } from 'date-fns';
import { EMPTY, filter, Observable, switchMap, tap } from 'rxjs';
import {
  AdvancedSearchMap,
  AdvancedSearchType,
  getSavedSearchFriendlyType,
  SavedAdvancedSearchItem,
} from './advanced-search.model';
import { AuthService } from './auth.service';
import { SaveSearchDialogComponent } from './components/save-search-dialog.component';
import {
  IAdvancedAddressSearchFormValues,
  IAdvancedCrownDescriptionSearchOptions,
  IAdvancedCrownDescriptionSimpleSearchOptions,
  IAdvancedLotSearchByPlanOptions,
  IAdvancedLotSearchByStreetOptions,
  IAdvancedPropertySearchCouncilNoOptions,
  IAdvancedSearchAddressPropertyOptions,
  IAdvancedSearchAddressRoadOptions,
  IAdvancedSurveyLabelGeometrySearchOptions,
  IAdvancedSurveyMarkSearchOptions,
} from './models/advanced-search.model';
import { ToastService } from './toast/toast.service';
@Injectable({
  providedIn: 'root',
})
export class AdvancedSearchService {
  private readonly apiService = inject(ApiService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly toastService = inject(ToastService);

  readonly pendingAutoRun = signal(false);

  saveSearch(searchType: AdvancedSearchType) {
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login'], {
        queryParams: { redirectTo: this.router.url },
      });
      return EMPTY;
    }

    const defaultName = `${getSavedSearchFriendlyType(searchType)} ${format(new Date(), 'dd/MM/yyyy')}`;
    return this.dialog
      .open(SaveSearchDialogComponent, {
        width: '90vw',
        maxWidth: '500px',
        data: { defaultName },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((name: string) =>
          this.apiService.invoke(createAdvancedSearchHistory, {
            body: {
              search_type: searchType,
              search_name: name,
              search_criteria: this.getSearchCriteria(searchType),
            },
          }),
        ),
      )
      .pipe(
        tap({
          next: () =>
            this.toastService.showSuccessMessage(
              'Search saved successfully',
              1500,
            ),
          error: () =>
            this.toastService.showErrorMessage('Failed to save search', 1500),
        }),
      );
  }

  getSavedSearches(): Observable<AdvancedSearchHistoryListDto> {
    return this.apiService.invoke(getAdvancedSearchHistory);
  }

  deleteSavedSearch(
    item: SavedAdvancedSearchItem,
    onSuccess?: () => void,
  ): void {
    this.apiService
      .invoke(deleteAdvancedSearchHistory, {
        searchId: Number(item.id),
      })
      .subscribe({
        next: () => {
          this.toastService.showSuccessMessage('Saved search deleted', 1500);
          onSuccess?.();
        },
        error: () => {
          this.toastService.showErrorMessage(
            'Failed to delete saved search',
            1500,
          );
        },
      });
  }

  getSavedSearchTypeDescription(searchType: string): string {
    return getSavedSearchFriendlyType(searchType);
  }

  openSavedSearch(item: SavedAdvancedSearchItem) {
    const searchType = item.search_type as AdvancedSearchType;
    const mapping = AdvancedSearchMap[searchType];

    if (!mapping) {
      this.toastService.showErrorMessage('Saved search type is not supported.');
      return;
    }

    const queryParams = this.parseSavedSearchCriteria(
      item.search_criteria,
      mapping.paramKeys,
    );
    if (!queryParams) {
      this.toastService.showErrorMessage('Saved search criteria is invalid.');
      return;
    }

    const destinationRoute = mapping.executeRoute ?? mapping.route;

    this.apiService
      .invoke(markAdvancedSearchHistoryAsUsed, {
        searchId: Number(item.id),
      })
      .subscribe({
        next: () => {
          if (mapping.executeRoute === mapping.route) {
            this.pendingAutoRun.set(true);
          }
          this.router.navigate(
            [
              '/map',
              {
                outlets: {
                  menu: ['landing', 'search', ...destinationRoute.split('/')],
                },
              },
            ],
            {
              queryParams,
              queryParamsHandling: 'merge',
            },
          );
        },
        error: () => {
          this.toastService.showErrorMessage('Failed to open saved search.');
        },
      });
  }

  runPendingAutoSearch(onAutoRun: () => boolean): void {
    if (!this.pendingAutoRun()) return;
    const didRun = onAutoRun();
    if (!didRun) return;
    this.pendingAutoRun.set(false);
  }

  private parseSavedSearchCriteria(
    criteria: string,
    allowedParamKeys: string[],
  ): Record<string, string> | null {
    try {
      const parsed: unknown = JSON.parse(criteria);

      if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
        return null;
      }

      const allowedParamKeySet = new Set(allowedParamKeys);

      return Object.fromEntries(
        Object.entries(parsed as Record<string, unknown>)
          .filter(([, value]) => value !== null && value !== undefined)
          .map(([key, value]) => [key, String(value)])
          .filter(([key]) => allowedParamKeySet.has(key)),
      );
    } catch {
      return null;
    }
  }

  private getSearchCriteria(searchType: AdvancedSearchType): string {
    const { paramKeys } = AdvancedSearchMap[searchType];
    const paramMap = this.activatedRoute.snapshot.queryParamMap;

    const criteriaObject = Object.fromEntries(
      paramKeys
        .map((key) => [key, paramMap.get(key)])
        .filter(([, value]) => value !== null),
    ) as Record<string, string>;

    return JSON.stringify(criteriaObject);
  }

  getAddressSearchFormValues(
    route: ActivatedRouteSnapshot,
  ): IAdvancedAddressSearchFormValues | undefined {
    const queryParam = route.queryParamMap.get('addrSearchFormData');
    if (!queryParam) {
      return undefined;
    }

    return JSON.parse(queryParam) as IAdvancedAddressSearchFormValues;
  }

  searchAddressForLocality(
    locality: string,
  ): Observable<SearchAddressResultDto> {
    console.log(`calling the locality search api with "${locality}"`);
    return this.apiService.invoke(searchForLocality, {
      locality,
    });
  }

  searchAddressForLocalityGeometry(
    pfi: string,
  ): Observable<SearchAddressGeometryDto> {
    return this.apiService.invoke(searchForLocalityGeometry, {
      pfi,
    });
  }

  searchAddressForRoad(
    options: IAdvancedSearchAddressRoadOptions,
  ): Observable<SearchAddressResultDto> {
    return this.apiService.invoke(searchForRoad, {
      road_name: options.roadName,
      road_type: options.roadType,
      road_suffix: options.roadSuffix,
      locality: options.locality,
    });
  }

  searchAddressForRoadGeometry(
    pfi: string,
  ): Observable<SearchAddressGeometryDto> {
    return this.apiService.invoke(searchForRoadGeometry, {
      pfi,
    });
  }

  searchAddressForRoadOptions(): Observable<SearchAddressRoadOptionsDto> {
    return this.apiService.invoke(getRoadSearchOptions);
  }

  searchAddressForProperty(
    options: IAdvancedSearchAddressPropertyOptions,
  ): Observable<SearchAddressResultDto> {
    return this.apiService.invoke(searchForProperty, {
      road_name: options.roadName,
      locality: options.locality,
      unit_number: options.unitNumber,
      road_number: options.roadNumber,
      road_type: options.roadType,
      road_suffix: options.roadSuffix,
    });
  }

  searchAddressForPropertyGeometry(
    pfiList: string[],
  ): Observable<SearchAddressGeometryDto> {
    return this.apiService.invoke(searchForPropertyGeometry, {
      pfi: pfiList,
    });
  }

  searchAddressForPropertyByAddressPfi(
    addressPfi: number,
  ): Observable<SearchAddressResultDto> {
    return this.apiService.invoke(searchForPropertyByAddressPfi, {
      addressPfi,
    });
  }

  searchParcelByViewPfi(viewPfi: string): Observable<SearchParcelResultDto> {
    return this.apiService.invoke(searchForParcelByViewPfi, {
      viewPfi: viewPfi,
    });
  }

  searchParcelByPfi(pfi: string): Observable<SearchParcelResultDto> {
    return this.apiService.invoke(searchForParcelByPfi, {
      pfi: pfi,
    });
  }

  searchParcelBySpi(spi: string): Observable<SearchParcelBySpiResultDto> {
    return this.apiService.invoke(searchForParcelBySpi, {
      spi,
    });
  }

  searchParcelGeometryByPfiList(
    pfiList: string[],
  ): Observable<SearchParcelGeometryDto> {
    return this.apiService.invoke(searchForParcelGeometryByPfiList, {
      pfi: pfiList,
    });
  }

  searchPropertyByCouncilPropertyNo(
    options: IAdvancedPropertySearchCouncilNoOptions,
  ): Observable<SearchPropertyByCouncilNoResultDto> {
    return this.apiService.invoke(searchForPropertyByCouncilPropertyNo, {
      property_number: options.propertyNumber,
      lga_code: options.lgaCode,
    });
  }

  searchPropertyGeometryByPfi(
    pfiList: string[],
  ): Observable<SearchPropertyGeometryDto> {
    return this.apiService.invoke(searchForPropertyGeometryByPfiList, {
      pfi: pfiList,
    });
  }

  searchPropertyLgaOptions(): Observable<SearchPropertyLgaOptionsDto> {
    return this.apiService.invoke(searchForPropertyByLgaOptions);
  }

  searchPropertyByPfi(pfi: string): Observable<SearchPropertyResultDto> {
    return this.apiService.invoke(searchForPropertyByPfi, {
      pfi,
    });
  }

  searchPropertyByViewPfi(
    viewPfi: string,
  ): Observable<SearchPropertyResultDto> {
    return this.apiService.invoke(searchForPropertyByViewPfi, {
      viewPfi,
    });
  }

  searchLotByPlan(
    options: IAdvancedLotSearchByPlanOptions,
  ): Observable<SearchLotResultDto> {
    return this.apiService.invoke(searchForLotByPlan, {
      plan: options.plan,
      lot: options.lot,
      type: options.type,
    });
  }

  searchLotByStreet(
    options: IAdvancedLotSearchByStreetOptions,
  ): Observable<SearchLotResultDto> {
    return this.apiService.invoke(searchForLotByStreet, {
      lot: options.lot,
      street_name: options.streetName,
      street_type: options.streetType,
      street_suffix: options.streetSuffix,
      locality: options.locality,
    });
  }

  searchForSurveyLabel(surveyLabel: string): Observable<SearchSurveyLabelDto> {
    return this.apiService.invoke(searchForSurveyLabel, {
      surveyLabel,
    });
  }

  searchSurveyLabelGeometryByPfiList(
    options: IAdvancedSurveyLabelGeometrySearchOptions,
  ): Observable<SearchParcelGeometryDto> {
    return this.apiService.invoke(searchForSurveyLabelGeometryByPfiList, {
      pfi: options.pfiList,
      survey_label: options.surveyLabel,
    });
  }

  searchForSurveyMark(
    options: IAdvancedSurveyMarkSearchOptions,
  ): Observable<SearchSurveyMarkDto> {
    return this.apiService.invoke(searchForSurveyMark, {
      mark_name: options.markName,
      mark_number: options.nineFigureNumber,
      stamped_id: options.stampedId,
    });
  }

  searchSurveyMarkGeometryByPfiList(
    pfiList: string[],
  ): Observable<SearchSurveyMarkGeometryDto> {
    return this.apiService.invoke(searchForSurveyMarkGeometryByPfiList, {
      pfi: pfiList,
    });
  }

  // used by search survey mark workflow
  getParishOptions(): Observable<ParishListResponseDto> {
    return this.apiService.invoke(getParishList);
  }

  getTownshipOptions(): Observable<TownshipListResponseDto> {
    return this.apiService.invoke(getTownshipList);
  }

  searchCrownDescriptionForGeometry(
    options: IAdvancedCrownDescriptionSimpleSearchOptions,
  ): Observable<SearchCrownDescriptionGeometryDto> {
    return this.apiService.invoke(searchForCrownDescriptionGeometry, {
      parish_or_township_code: options.parishOrTownshipCode,
      parish_or_township_name: options.parishOrTownshipName,
    });
  }

  searchCrownDescriptionForParcels(
    options: IAdvancedCrownDescriptionSearchOptions,
  ): Observable<SearchCrownDescriptionParcelResultDto> {
    return this.apiService.invoke(searchForCrownDescriptionParcels, {
      parish_or_township_code: options.parishOrTownshipCode,
      parish_or_township_name: options.parishOrTownshipName,
      allotment: options.allotment,
      section: options.section,
      subdivision: options.subdivision,
      portion: options.portion,
      block: options.block,
    });
  }
}
